import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ListingCard from './components/ListingCard';
import { useListings } from '@/hooks/useListings';
import SEO from '@/components/feature/SEO';

const filterOptions = ['All', 'Single Family', 'Condo', 'Luxury Estate', 'Beach House', 'Penthouse', 'Townhouse', 'Land'];

const listingsSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Florida Homes for Sale – Featured Listings',
  description: 'Browse Florida real estate listings including single family homes, condos, luxury estates, and beach houses for sale.',
  url: `${import.meta.env.VITE_SITE_URL || 'https://floridahousesforsale.deals'}/listings`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL || 'https://floridahousesforsale.deals' },
      { '@type': 'ListItem', position: 2, name: 'Listings', item: `${import.meta.env.VITE_SITE_URL || 'https://floridahousesforsale.deals'}/listings` },
    ],
  },
};

export default function ListingsPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const { listings } = useListings();

  const filtered = activeFilter === 'All'
    ? listings
    : listings.filter((l) => l.type === activeFilter);

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Florida Real Estate Listings | Homes for Sale | Amparo Jarquin"
        description="Browse Florida homes for sale — single family, condos, luxury estates, beach houses. Find your perfect Florida property with REALTOR® Amparo Jarquin."
        keywords="Florida real estate listings, homes for sale Florida, Florida condos, luxury homes Florida, beach houses Florida"
        canonical="/listings"
        ogImage="https://public.readdy.ai/ai/img_res/ccea8c8cd67ab470892cae2e7b08f5aa.jpg"
        schema={listingsSchema}
      />
      <Navbar />

      {/* Page Header */}
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20Florida%20coastal%20neighborhoods%2C%20beautiful%20homes%2C%20blue%20ocean%2C%20palm%20trees%2C%20sunny%20day%2C%20tropical%20real%20estate%20community%20overhead%20shot&width=1600&height=500&seq=listbg001&orientation=landscape"
            alt="Florida listings"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-stone-900/65"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-3">Browse Properties</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Featured Listings</h1>
          <p className="text-white/70 max-w-xl mx-auto text-base">
            Discover exceptional Florida properties curated by Amparo Jarquin
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-20 z-30 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3 overflow-x-auto">
          {filterOptions.map((opt) => (
            <button
              key={opt}
              onClick={() => setActiveFilter(opt)}
              className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                activeFilter === opt
                  ? 'bg-amber-500 text-white'
                  : 'bg-stone-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </section>

      {/* Listings Grid */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-product-shop>
        <p className="text-gray-400 text-sm mb-8">{filtered.length} properties found</p>
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
              <i className="ri-home-3-line text-amber-400 text-2xl"></i>
            </div>
            <p className="text-gray-600 font-semibold mb-1">No listings in this category</p>
            <p className="text-gray-400 text-sm">Check back soon or browse all properties</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
